import Typography from '@mui/material/Typography';
import { Control, useFieldArray } from 'react-hook-form';
import { CreateNoticeSchemaFormValues } from '../schema';
import { PerInvestorBreakdownEntry } from '../useCapitalActionNotice/types';
import { EmailOutlined, SaveAlt } from '@mui/icons-material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Avatar from 'further-ui/components/Avatar';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import Switch from 'further-ui/components/Switch';
import {
  numberToCurrencyString,
  numberToPercentageString,
  downloadFileFromTrpc,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api, { ApiInputs, ApiResponses } from 'lib/trpcClient';
import { CircularProgress } from '@mui/material';
import { useState } from 'react';

type Props = {
  currencyLcy: CurrencyCode;
  control: Control<CreateNoticeSchemaFormValues>;
  data: Array<PerInvestorBreakdownEntry>;
  vehicleId: string;
  capitalActionId: string;
};

const PerInvestorBreakdown: React.FC<Props> = ({
  currencyLcy,
  data,
  vehicleId,
  capitalActionId,
  control,
}) => {
  const [inFlightInvestorPdfDownloads, setInFlightInvestorPdfDownloads] =
    useState<Array<string>>([]);
  const { mutateAsync: downloadPdf } =
    api.capitalActionNotice.downloadPdf.useMutation(
      useHandleTrpcMutation<
        ApiResponses['capitalActionNotice']['downloadPdf'],
        ApiInputs['capitalActionNotice']['downloadPdf']
      >()({
        onMutate: ({ investorId }) => {
          setInFlightInvestorPdfDownloads((prev) => [...prev, investorId]);
        },
        onSuccess: ({ file, filename, mimeType }) => {
          downloadFileFromTrpc({
            content: file,
            name: filename,
            mimeType,
          });
        },
        onSettled: (_, __, input) => {
          setInFlightInvestorPdfDownloads((prev) =>
            prev.filter((id) => id !== input.investorId),
          );
        },
      }),
    );
  const { update, fields } = useFieldArray({
    control,
    name: 'perInvestorBreakdown',
    keyName: 'fieldId',
  });

  const toggleEmailNotification = (investorId: string) => {
    const index = fields.findIndex((field) => field.id === investorId);
    if (index === -1) return;
    const data = fields[index];
    update(index, { ...data, notifyViaEmail: !data.notifyViaEmail });
  };

  const columns: Array<TableColumn<PerInvestorBreakdownEntry>> = [
    {
      label: 'Investor',
      key: 'id',
      render: ({ investorName }) => (
        <Row centered spacing="xs">
          <Avatar color="random" size="md" text={investorName} />
          <Typography variant="inherit">{investorName}</Typography>
        </Row>
      ),
      footer: `Total from ${data.length} investors`,
    },
    {
      label: 'Capital call',
      key: 'capitalCalledLcy',
      render: ({ capitalCalledLcy }) =>
        numberToCurrencyString(capitalCalledLcy, { currency: currencyLcy }),
      footer: numberToCurrencyString(
        data.reduce((sum, entry) => sum + entry.capitalCalledLcy, 0),
        { currency: currencyLcy },
      ),
    },
    {
      label: 'Distribution / capital refund',
      key: 'capitalDistributedLcy',
      render: ({ capitalDistributedLcy }) =>
        numberToCurrencyString(capitalDistributedLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(
        data.reduce((sum, entry) => sum + entry.capitalDistributedLcy, 0),
        { currency: currencyLcy },
      ),
    },
    {
      label: 'Total due to / (from) fund',
      key: 'totalDueLcy',
      render: ({ capitalCalledLcy, capitalDistributedLcy }) => {
        const totalDueLcy = capitalCalledLcy - capitalDistributedLcy;
        return numberToCurrencyString(totalDueLcy, { currency: currencyLcy });
      },
      footer: numberToCurrencyString(
        data.reduce(
          (sum, entry) =>
            sum + (entry.capitalCalledLcy - entry.capitalDistributedLcy),
          0,
        ),
        { currency: currencyLcy },
      ),
    },
    {
      label: 'Unfunded commitment (post-notice)',
      key: 'unfundedCommitmentLcy',
      render: ({ unfundedCommitmentLcy, unfundedCommitmentPercentage }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(unfundedCommitmentLcy, {
              currency: currencyLcy,
            })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToPercentageString(unfundedCommitmentPercentage, {
              noMultiply: true,
              accuracy: 2,
            })}
          </Typography>
        </Column>
      ),
      footer: numberToCurrencyString(
        data.reduce((sum, entry) => sum + entry.unfundedCommitmentLcy, 0),
        { currency: currencyLcy },
      ),
    },
    {
      label: 'Email notification',
      key: 'notifyViaEmail',
      render: ({ id, includedInNotice }) => (
        <Row centered justify="space-between">
          <Switch
            edge="start"
            checked={
              includedInNotice
                ? Boolean(
                    fields.find((field) => field.id === id)?.notifyViaEmail,
                  )
                : false
            }
            disabled={!includedInNotice}
            onChange={() => {
              toggleEmailNotification(id);
            }}
          />
          {inFlightInvestorPdfDownloads.includes(id) ? (
            <CircularProgress size={15} />
          ) : null}
        </Row>
      ),
    },
  ];
  return (
    <Column spacing="sm">
      <Typography variant="h5">Per-investor breakdown</Typography>
      <Table
        emptyMessage="No investors"
        rows={data}
        actions={[
          {
            icon: SaveAlt,
            label: 'Download preview PDF',
            onClick: ({ id }) => {
              downloadPdf({ investorId: id, capitalActionId, vehicleId });
            },
            disabled: ({ includedInNotice }) => !includedInNotice,
            tooltip: ({ includedInNotice }) =>
              !includedInNotice
                ? 'This investor is not included in this capital action and will not be notified.'
                : undefined,
          },
          {
            icon: EmailOutlined,
            label: ({ id }) => {
              const fieldData = fields.find((field) => field.id === id);
              return fieldData?.notifyViaEmail
                ? 'Exclude from email notification'
                : 'Include in email notification';
            },
            tooltip: ({ includedInNotice }) =>
              !includedInNotice
                ? 'This investor is not included in this capital action and will not be notified.'
                : undefined,
            onClick: ({ id }) => {
              toggleEmailNotification(id);
            },
            disabled: ({ includedInNotice }) => !includedInNotice,
          },
        ]}
        columns={columns}
      />
    </Column>
  );
};

export default PerInvestorBreakdown;
